import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { theme } from '@src/common/styles/Theme';
import {
  NoteContentField,
  NoteContextField,
  SendNoteByMailField,
} from '@src/notes/NoteFields';
import { FormMultipleOptions } from '@src/orders/components/FormMultipleOptions';
import { noteStatusColor } from '@src/orders/components/note/NoteStatusColor';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useEffect } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NoteContext } from './domain/NoteContext';

interface Props {
  control: Control;
  setValue: (
    name: typeof SendNoteByMailField.fieldName,
    value: boolean,
  ) => void;
  context?: NoteContext;
  placeholder?: string;
}

const assertOption = (
  option: unknown,
): option is { value: string; label: string } => {
  if (!option || typeof option !== 'object') return false;

  const hasValue = 'value' in option && typeof option.value === 'string';
  const hasLabel = 'label' in option && typeof option.label === 'string';

  return hasValue && hasLabel;
};

export const AddNoteForm = ({
  control,
  setValue,
  context,
  placeholder,
}: Props) => {
  const { t } = useTranslation();
  const noteContextValues = useWatch({
    control,
    name: NoteContextField.fieldName,
  }) as string[] | undefined;

  const isClientContextSelected = noteContextValues?.includes(
    NoteContext.client.valueOf(),
  );

  useEffect(() => {
    if (!isClientContextSelected) {
      setValue(SendNoteByMailField.fieldName, false);
    }
  }, [isClientContextSelected, setValue]);

  return (
    <Stack gap={theme.spacing['6']}>
      <FormMultipleOptions
        control={control}
        placeholder={t('notes.tagsPlaceholder')}
        fieldName={NoteContextField.fieldName}
        alwaysShrinkLabel
        readOnly={
          context === NoteContext.cancel ||
          context === NoteContext.onHold ||
          context === NoteContext.adjustments ||
          context === NoteContext.error ||
          context === NoteContext.client
        }
        options={NoteContextField.options.map((option) => ({
          value: option,
          label: t(`fieldOptions.${option}`),
        }))}
        renderTags={(options) =>
          options.map((option) => {
            return assertOption(option) ? (
              <Stack key={option.value} marginRight={theme.spacing['2']}>
                <StatusBadge
                  radiusFull
                  size="large"
                  text={t(`fieldOptions.${option.value}`)}
                  color={noteStatusColor[option.value].color}
                  borderColor={noteStatusColor[option.value].border}
                  backgroundColor={noteStatusColor[option.value].background}
                />
              </Stack>
            ) : null;
          })
        }
      />
      <ControllerFormTextInput
        control={control}
        fieldName={NoteContentField.fieldName}
        alwaysShrinkLabel
        multiline
        minRows={4}
        placeholder={
          placeholder ||
          t('notes.notePlaceholder', {
            context: context?.valueOf(),
          })
        }
      />
      {isClientContextSelected && (
        <ControllerFormCheckbox
          control={control}
          fieldName={SendNoteByMailField.fieldName}
          label={t('notes.sendByMail')}
        />
      )}
    </Stack>
  );
};
